import { useModal } from '@/contexts/modal'
import AddIcons from '@/icons/AddIcons'
import { getCustomerMailers } from '@/modules/Customer/services'
import { CustomerMailer } from '@/modules/Customer/types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import DefaultMailerView from './DefaultMailerView'
import GroupedMailerView from './GroupedMailerView'
import { DataTable } from '@/components/ui/data-table'
import { createColumns } from './MailerColumns'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { X, LayoutGrid, List as ListIcon } from 'lucide-react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import EmailHistory from '../EmailHistory'

const List = () => {
    const { id } = useParams()
    const { t } = useTranslation('customer')
    const { setModal } = useModal()
    const [viewMode, setViewMode] = useState<
        'default' | 'by-recipients' | 'by-purpose'
    >('default')
    const [displayType, setDisplayType] = useState<'card' | 'table'>('card')
    const [selectedEmail, setSelectedEmail] = useState<string | null>(null)

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS_MAILERS, Number(id)],
        queryFn: () => getCustomerMailers({ customerID: Number(id) }),
    })

    // Group mailers by recipients
    const groupedByRecipients = useMemo(() => {
        if (!data?.results) return {}
        const groups: Record<string, CustomerMailer[]> = {}

        data.results.forEach((mailer) => {
            const recipients = mailer.mailer_receiver || ''
            if (!groups[recipients]) {
                groups[recipients] = []
            }
            groups[recipients].push(mailer)
        })
        return groups
    }, [data?.results])

    // Group mailers by purpose (mailer_type) - avoid duplicates
    const groupedByPurpose = useMemo(() => {
        if (!data?.results) return {}
        const groups: Record<string, Map<number, CustomerMailer>> = {}

        data.results.forEach((mailer) => {
            // Get unique purposes for this mailer
            const uniquePurposes = new Set(
                mailer.mailer_types?.map((type) => type.mailer_description) ||
                    []
            )

            uniquePurposes.forEach((purpose) => {
                if (!groups[purpose]) {
                    groups[purpose] = new Map()
                }
                // Use mailer ID as key to ensure uniqueness
                groups[purpose].set(mailer.customer_mailer_id, mailer)
            })
        })

        // Convert Maps back to arrays
        const result: Record<string, CustomerMailer[]> = {}
        Object.entries(groups).forEach(([purpose, mailerMap]) => {
            result[purpose] = Array.from(mailerMap.values())
        })
        return result
    }, [data?.results])

    // Filter grouped data based on selected email
    const filteredGroupedByRecipients = useMemo(() => {
        if (!selectedEmail) return groupedByRecipients
        const filtered: Record<string, CustomerMailer[]> = {}
        Object.entries(groupedByRecipients).forEach(([recipients, mailers]) => {
            if (recipients.includes(selectedEmail)) {
                filtered[recipients] = mailers
            }
        })
        return filtered
    }, [groupedByRecipients, selectedEmail])

    const filteredGroupedByPurpose = useMemo(() => {
        if (!selectedEmail) return groupedByPurpose
        const filtered: Record<string, CustomerMailer[]> = {}
        Object.entries(groupedByPurpose).forEach(([purpose, mailers]) => {
            const hasSelectedEmail = mailers.some((mailer) =>
                mailer.mailer_receiver?.includes(selectedEmail)
            )
            if (hasSelectedEmail) {
                filtered[purpose] = mailers
            }
        })
        return filtered
    }, [groupedByPurpose, selectedEmail])

    // Filter default view mailers
    const filteredMailers = useMemo(() => {
        if (!selectedEmail || !data?.results) return data?.results || []
        return data.results.filter((mailer) =>
            mailer.mailer_receiver?.includes(selectedEmail)
        )
    }, [data?.results, selectedEmail])

    const handleEditMailer = (mailer: CustomerMailer) => {
        setModal(
            'CustomerModals/MailerModals/CreateCustomerMailerModal/index',
            {
                action: 'update',
                customerID: Number(id),
                customerMailer: mailer,
            }
        )
    }

    const handleAddMailer = () => {
        setModal(
            'CustomerModals/MailerModals/CreateCustomerMailerModal/index',
            {
                customerID: Number(id),
            }
        )
    }

    const handleEmailClick = (email: string) => {
        setSelectedEmail(selectedEmail === email ? null : email)
    }

    // Create columns for data table
    const columns = useMemo(
        () =>
            createColumns({
                t,
                onEdit: handleEditMailer,
                onEmailClick: handleEmailClick,
                selectedEmail,
            }),
        [t, selectedEmail]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const mailerCount = data?.results?.length || 0

    return (
        <div className="space-y-3">
            {/* Header Bar */}
            <div className="bg-white rounded-lg border p-3">
                <div className="flex items-center justify-between gap-4">
                    {/* Left: Tabs */}
                    <Tabs
                        value={displayType === 'table' ? 'default' : viewMode}
                        onValueChange={(value) => {
                            if (displayType === 'table') {
                                setDisplayType('card')
                            }
                            setViewMode(
                                value as
                                    | 'default'
                                    | 'by-recipients'
                                    | 'by-purpose'
                            )
                        }}
                    >
                        <TabsList className="h-9">
                            <TabsTrigger
                                value="default"
                                className="text-xs px-3"
                            >
                                {t('mailer.views.default')}
                                {mailerCount > 0 && (
                                    <Badge
                                        variant="secondary"
                                        className="ml-1.5 h-5 px-1.5 text-[10px]"
                                    >
                                        {mailerCount}
                                    </Badge>
                                )}
                            </TabsTrigger>
                            <TabsTrigger
                                value="by-recipients"
                                className="text-xs px-3"
                            >
                                {t('mailer.views.recipients')}
                            </TabsTrigger>
                            <TabsTrigger
                                value="by-purpose"
                                className="text-xs px-3"
                            >
                                {t('mailer.views.purpose')}
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2">
                        {/* Filter indicator */}
                        {selectedEmail && (
                            <Badge
                                variant="secondary"
                                className="gap-1 cursor-pointer hover:bg-destructive/10"
                                onClick={() => setSelectedEmail(null)}
                            >
                                {selectedEmail}
                                <X className="h-3 w-3" />
                            </Badge>
                        )}

                        {/* View toggle */}
                        <div className="flex items-center rounded-md border p-0.5">
                            <Button
                                variant={
                                    displayType === 'card'
                                        ? 'secondary'
                                        : 'ghost'
                                }
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => setDisplayType('card')}
                            >
                                <LayoutGrid className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                variant={
                                    displayType === 'table'
                                        ? 'secondary'
                                        : 'ghost'
                                }
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => setDisplayType('table')}
                            >
                                <ListIcon className="h-3.5 w-3.5" />
                            </Button>
                        </div>

                        <Button
                            onClick={handleAddMailer}
                            size="sm"
                            className="gap-1.5"
                        >
                            <AddIcons className="w-3.5 h-3.5" />
                            {t('mailer.add')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg border p-4">
                {data?.results && data.results.length > 0 ? (
                    <>
                        {displayType === 'table' ? (
                            <DataTable
                                columns={columns}
                                data={filteredMailers}
                                searchKey="customer_mailer_name"
                                searchPlaceholder={t('mailer.search')}
                            />
                        ) : (
                            <>
                                {viewMode === 'default' && (
                                    <DefaultMailerView
                                        mailers={filteredMailers}
                                        onEdit={handleEditMailer}
                                        onEmailClick={handleEmailClick}
                                        selectedEmail={selectedEmail}
                                    />
                                )}
                                {viewMode === 'by-recipients' && (
                                    <GroupedMailerView
                                        groupedData={
                                            filteredGroupedByRecipients
                                        }
                                        mode="recipients"
                                        onEdit={handleEditMailer}
                                        onEmailClick={handleEmailClick}
                                        selectedEmail={selectedEmail}
                                    />
                                )}
                                {viewMode === 'by-purpose' && (
                                    <GroupedMailerView
                                        groupedData={filteredGroupedByPurpose}
                                        mode="purpose"
                                        onEdit={handleEditMailer}
                                        onEmailClick={handleEmailClick}
                                        selectedEmail={selectedEmail}
                                    />
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <div className="text-center py-12">
                        <div className="w-12 h-12 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                            <AddIcons className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <h3 className="font-medium text-foreground mb-1">
                            {t('mailer.empty')}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('mailer.empty-desc')}
                        </p>
                        <Button
                            onClick={handleAddMailer}
                            size="sm"
                            className="gap-1.5"
                        >
                            <AddIcons className="w-3.5 h-3.5" />
                            {t('mailer.add')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Email History */}
            <EmailHistory customerId={Number(id)} />
        </div>
    )
}

export default List
